import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { vocabApi } from '@/api/vocab';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

/**
 * StudentCueCardsPage — the "Учить слова" launcher. It trains the student's OWN
 * saved vocabulary (personal dictionary) with the adaptive 5-mode SRS engine.
 * There are no curator-authored sets in this product; learning words lives in
 * the vocabulary section, so this page just starts a personal session.
 */
export function StudentCueCardsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stats = useQuery({ queryKey: ['vocab', 'stats'], queryFn: vocabApi.getStats });
  const total = stats.data?.total ?? 0;
  const learned = stats.data?.learned ?? 0;
  const toLearn = Math.max(total - learned, 0);

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeader
        title={t('myWords.title')}
        subtitle={t('myWords.subtitle')}
      />

      <Card className="relative overflow-hidden bg-gradient-to-br from-primary to-primary-pressed text-on-primary">
        <p className="text-[11px] font-semibold uppercase tracking-label text-on-primary/80">{t('myWords.myDict')}</p>
        <div className="mt-2 flex items-end gap-6">
          <div>
            <div className="font-display text-4xl font-extrabold tabular-nums">{toLearn}</div>
            <div className="text-sm text-on-primary/90">{t('myWords.toLearn')}</div>
          </div>
          <div>
            <div className="font-display text-4xl font-extrabold tabular-nums">{learned}</div>
            <div className="text-sm text-on-primary/90">{t('myWords.learned')}</div>
          </div>
        </div>
        <Button
          variant="secondary"
          // Белая кнопка на оранжевом — так в макете; пишем цвет прямо,
          // чтобы правило песочных полотен её не трогало.
          className="mt-5 !bg-white !text-primary"
          disabled={total === 0}
          onClick={() => navigate('/app/cue-cards/my')}
        >
          <span className="material-symbols-outlined">school</span>
          {t(toLearn > 0 ? 'myWords.start' : 'myWords.repeat')}
        </Button>
      </Card>

      {total === 0 && (
        <Card variant="subtle" className="flex flex-col items-center gap-3 py-8 text-center">
          <CardTitle>{t('myWords.empty')}</CardTitle>
          <p className="max-w-sm text-sm text-on-surface-variant">
            {t('myWords.emptyHint')}
          </p>
          <Button variant="secondary" onClick={() => navigate('/app/vocabulary')}>
            <span className="material-symbols-outlined">menu_book</span>
            {t('myWords.toVocab')}
          </Button>
        </Card>
      )}
    </div>
  );
}
